import React, { useState, useEffect, useCallback } from 'react';
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { useChannelOptions, useVerticalOptions, usePlatformOptions } from "@/hooks/useVocabulary";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MoreHorizontal,
  Edit2,
  Power,
  PowerOff,
  Tag,
  AlertCircle,
  Key,
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const CHANNEL_COLORS = {
  paid_traffic: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  referral_traffic: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  unclassified: "bg-slate-500/20 text-slate-300 border-slate-500/30",
};

const PLATFORM_COLORS = {
  meta: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  google: "bg-red-500/20 text-red-300 border-red-500/30",
  ghl: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  referral_partner: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  organic: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  other: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};

/**
 * Lead Source Admin Manager
 * Admin UI for managing canonical lead sources for routing and reporting.
 */
/**
 * Lead Source Admin Manager
 * Admin UI for managing canonical lead sources for routing and reporting.
 * 
 * P0 TENANT ISOLATION: Accepts tenantId prop for SuperAdmin cross-tenant access.
 * Refetches data and resets state when tenantId changes.
 */
const LeadSourceAdminManager = ({ className = "", tenantId }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enums, setEnums] = useState({}); // Platform/Channel/Status enums from backend
  
  // P1 VOCABULARY: Use vocabulary hooks for dropdowns
  const { options: channelOptions, isLoading: channelsLoading } = useChannelOptions();
  const { options: verticalOptions, isLoading: verticalsLoading } = useVerticalOptions();
  const { options: platformOptions, isLoading: platformsLoading } = usePlatformOptions();
  
  // Workspaces for dropdown
  const [workspaces, setWorkspaces] = useState([]);
  
  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSource, setEditSource] = useState(null);
  
  // API Key management states
  const [keyDialog, setKeyDialog] = useState({ open: false, source: null, action: null });
  const [newApiKey, setNewApiKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    key: "",
    name: "",
    platform: "meta",
    channel: "paid_traffic",
    default_playbook_key: "",
    default_workspace_id: "",
    default_vertical_key: "",  // P1: Vertical attribution
  });
  const [submitting, setSubmitting] = useState(false);
  const [keyError, setKeyError] = useState("");

  // P0 TENANT ISOLATION: Build params with tenant_id for SuperAdmin cross-tenant access
  const buildTenantParams = useCallback(() => {
    return tenantId ? { tenant_id: tenantId } : {};
  }, [tenantId]);

  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Reset error at start of fetch
      const params = buildTenantParams();
      const [sourcesRes, enumsRes, workspacesRes] = await Promise.all([
        api.get('/admin/lead-sources', { params }),
        api.get('/admin/lead-sources/enums', { params }),
        api.get('/tenants/workspaces', { params }),
      ]);
      // Ensure sources is always an array
      const sourcesData = Array.isArray(sourcesRes.data) ? sourcesRes.data : [];
      setSources(sourcesData);
      setEnums(enumsRes.data);
      setWorkspaces(Array.isArray(workspacesRes.data) ? workspacesRes.data : []);
    } catch (err) {
      console.error('[LeadSourceAdminManager] fetchSources error:', err);
      setError(err.response?.data?.detail || 'Failed to load lead sources');
    } finally {
      setLoading(false);
    }
  }, [buildTenantParams]);

  // P0 TENANT ISOLATION: Refetch data and reset local state when tenant changes
  useEffect(() => {
    // Reset dialog/form state on tenant change to prevent stale data
    setCreateOpen(false);
    setEditOpen(false);
    setEditSource(null);
    setKeyDialog({ open: false, source: null, action: null });
    setNewApiKey(null);
    setFormData({
      key: "",
      name: "",
      platform: "meta",
      channel: "paid_traffic",
      default_playbook_key: "",
      default_workspace_id: "",
      default_vertical_key: "",
    });
    setKeyError("");
    
    // Fetch fresh data for new tenant
    fetchSources();
  }, [tenantId, fetchSources]);

  const validateKey = (value) => {
    if (!value) {
      setKeyError("Key is required");
      return false;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(value)) {
      setKeyError("Key must be lowercase snake_case (e.g., mp_meta_paid)");
      return false;
    }
    setKeyError("");
    return true;
  };

  const handleCreate = async () => {
    if (!validateKey(formData.key)) return;
    if (!formData.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    
    try {
      setSubmitting(true);
      await api.post('/admin/lead-sources', {
        key: formData.key.toLowerCase().trim(),
        name: formData.name.trim(),
        platform: formData.platform,
        channel: formData.channel,
        default_playbook_key: formData.default_playbook_key || null,
        default_workspace_id: formData.default_workspace_id || null,
        default_vertical_key: formData.default_vertical_key || null,
      });
      
      toast({ title: "Lead source created" });
      setFormData({
        key: "",
        name: "",
        platform: "meta",
        channel: "paid_traffic",
        default_playbook_key: "",
        default_workspace_id: "",
      });
      setCreateOpen(false);
      fetchSources();
      
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes("already exists")) {
        setKeyError(detail);
      } else {
        toast({
          title: "Failed to create lead source",
          description: detail || "Please try again",
          variant: "destructive",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editSource) return;
    if (!formData.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    
    try {
      setSubmitting(true);
      await api.put(`/admin/lead-sources/${editSource.id}`, {
        name: formData.name.trim(),
        platform: formData.platform,
        channel: formData.channel,
        default_playbook_key: formData.default_playbook_key || null,
        default_workspace_id: formData.default_workspace_id || null,
        default_vertical_key: formData.default_vertical_key || null,
      });
      
      toast({ title: "Lead source updated" });
      setEditOpen(false);
      setEditSource(null);
      fetchSources();
      
    } catch (err) {
      toast({
        title: "Failed to update lead source",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (source) => {
    const endpoint = source.status === "active" 
      ? `/admin/lead-sources/${source.id}/deactivate`
      : `/admin/lead-sources/${source.id}/activate`;
    
    try {
      await api.post(endpoint);
      fetchSources();
      toast({
        title: `Lead source ${source.status === "active" ? "deactivated" : "activated"}`,
      });
    } catch (err) {
      toast({
        title: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (source) => {
    setEditSource(source);
    setFormData({
      key: source.key,
      name: source.name,
      platform: source.platform,
      channel: source.channel,
      default_playbook_key: source.default_playbook_key || "",
      default_workspace_id: source.default_workspace_id || "",
      default_vertical_key: source.default_vertical_key || "",
    });
    setEditOpen(true);
  };

  // ============================================================================
  // API KEY MANAGEMENT
  // ============================================================================
  
  const handleGenerateKey = async () => {
    if (!keyDialog.source) return;
    
    setKeyLoading(true);
    setNewApiKey(null);
    
    try {
      const res = await api.post(`/admin/lead-sources/${keyDialog.source.id}/generate-key`);
      setNewApiKey(res.data.api_key);
      toast({
        title: "API Key Generated",
        description: "Store this key securely - it won't be shown again.",
      });
      fetchSources();
    } catch (err) {
      toast({
        title: "Failed to generate key",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setKeyLoading(false);
    }
  };
  
  const handleRotateKey = async () => {
    if (!keyDialog.source) return;
    
    setKeyLoading(true);
    setNewApiKey(null);
    
    try {
      const res = await api.post(`/admin/lead-sources/${keyDialog.source.id}/rotate-key`);
      setNewApiKey(res.data.api_key);
      toast({
        title: "API Key Rotated",
        description: "The old key is now invalid. Store this new key securely.",
      });
      fetchSources();
    } catch (err) {
      toast({
        title: "Failed to rotate key",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setKeyLoading(false);
    }
  };
  
  const handleRevokeKey = async () => {
    if (!keyDialog.source) return;
    
    setKeyLoading(true);
    
    try {
      await api.delete(`/admin/lead-sources/${keyDialog.source.id}/revoke-key`);
      toast({
        title: "API Key Revoked",
        description: "Lead source can no longer accept intake webhooks.",
      });
      setKeyDialog({ open: false, source: null, action: null });
      fetchSources();
    } catch (err) {
      toast({
        title: "Failed to revoke key",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setKeyLoading(false);
    }
  };
  
  const [copiedBearerKey, setCopiedBearerKey] = useState(false);
  
  const copyApiKey = async () => {
    if (!newApiKey) return;
    try {
      await navigator.clipboard.writeText(newApiKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };
  
  const copyBearerKey = async () => {
    if (!newApiKey) return;
    try {
      await navigator.clipboard.writeText(`Bearer ${newApiKey}`);
      setCopiedBearerKey(true);
      setTimeout(() => setCopiedBearerKey(false), 2000);
      toast({ title: "Copied!", description: "Bearer token copied to clipboard" });
    } catch (err) {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };
  
  const openKeyDialog = (source, action) => {
    setNewApiKey(null);
    setCopiedKey(false);
    setKeyDialog({ open: true, source, action });
  };
  
  const closeKeyDialog = () => {
    setKeyDialog({ open: false, source: null, action: null });
    setNewApiKey(null);
    setCopiedKey(false);
    setCopiedBearerKey(false);
  };

  const getWorkspaceName = (workspaceId) => {
    const ws = workspaces.find(w => w.id === workspaceId);
    return ws?.name || workspaceId?.substring(0, 8) + "...";
  };

  // Format relative timestamp for display
  const formatRelativeTime = (isoStr) => {
    if (!isoStr) return "Never";
    const date = new Date(isoStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className={`glass border-white/10 ${className}`}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className} data-testid="lead-source-admin-manager">
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Tag className="h-5 w-5 text-indigo-400" />
                Lead Sources
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">
                Configure lead sources for routing and War Room reporting
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setFormData({
                  key: "",
                  name: "",
                  platform: "meta",
                  channel: "paid_traffic",
                  default_playbook_key: "",
                  default_workspace_id: "",
                });
                setKeyError("");
                setCreateOpen(true);
              }}
              size="sm"
              data-testid="create-lead-source-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Lead Source
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-red-400 p-4 bg-red-500/10 rounded-lg">
              {error}
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Tag className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No lead sources configured</p>
              <p className="text-xs mt-1">Create lead sources to classify incoming leads</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-slate-400">Key</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Platform</TableHead>
                  <TableHead className="text-slate-400">Channel</TableHead>
                  <TableHead className="text-slate-400">API Key</TableHead>
                  <TableHead className="text-slate-400">Last Received</TableHead>
                  <TableHead className="text-slate-400">Default Workspace</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((source) => (
                  <TableRow 
                    key={source.id} 
                    className="border-white/10" 
                    data-testid={`lead-source-row-${source.id}`}
                  >
                    <TableCell className="font-mono text-sm text-slate-300">
                      {source.key}
                    </TableCell>
                    <TableCell className="font-medium text-slate-200">
                      {source.name}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={PLATFORM_COLORS[source.platform] || PLATFORM_COLORS.other}
                      >
                        {platformOptions.find(p => p.value === source.platform)?.label || source.platform}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={CHANNEL_COLORS[source.channel] || CHANNEL_COLORS.unclassified}
                      >
                        {channelOptions.find(c => c.value === source.channel)?.label || source.channel}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {source.has_api_key ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-400" />
                          <span className="font-mono text-xs text-slate-400">
                            {source.api_key_fingerprint}...
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-slate-500" />
                          <span className="text-xs text-slate-500">None</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm" data-testid={`last-received-${source.id}`}>
                      {source.last_received_at ? (
                        <span title={new Date(source.last_received_at).toLocaleString()}>
                          {formatRelativeTime(source.last_received_at)}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {source.default_workspace_id 
                        ? getWorkspaceName(source.default_workspace_id)
                        : <span className="text-slate-500">—</span>
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={source.status === "active"}
                          onCheckedChange={() => handleToggleStatus(source)}
                          aria-label={`Toggle ${source.name} status`}
                          data-testid={`toggle-status-${source.id}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon-sm"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-950 border-white/10 ring-1 ring-white/10">
                          <DropdownMenuItem
                            onClick={() => openEditDialog(source)}
                            className="text-slate-200 focus:bg-slate-800 focus:text-slate-100"
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-white/10" />
                          {/* API Key Management */}
                          {source.has_api_key ? (
                            <>
                              <DropdownMenuItem
                                onClick={() => openKeyDialog(source, "rotate")}
                                className="text-slate-200 focus:bg-slate-800 focus:text-slate-100"
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Rotate API Key
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openKeyDialog(source, "revoke")}
                                className="text-red-400 focus:bg-red-500/10 focus:text-red-300"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Revoke API Key
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => openKeyDialog(source, "generate")}
                              className="text-slate-200 focus:bg-slate-800 focus:text-slate-100"
                            >
                              <Key className="h-4 w-4 mr-2" />
                              Generate API Key
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem
                            onClick={() => handleToggleStatus(source)}
                            className="text-slate-200 focus:bg-slate-800 focus:text-slate-100"
                          >
                            {source.status === "active" ? (
                              <>
                                <PowerOff className="h-4 w-4 mr-2" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <Power className="h-4 w-4 mr-2" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Create Lead Source</DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure a new lead source for routing and reporting
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key" className="text-slate-200">
                Key <span className="text-red-400">*</span>
              </Label>
              <Input
                id="key"
                placeholder="e.g., mp_meta_paid"
                value={formData.key}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, key: e.target.value }));
                  validateKey(e.target.value);
                }}
                className="bg-white/5 border-white/10 text-slate-100 font-mono"
                data-testid="lead-source-key-input"
              />
              {keyError && (
                <div className="flex items-center gap-1 text-xs text-red-400">
                  <AlertCircle className="h-3 w-3" />
                  {keyError}
                </div>
              )}
              <p className="text-xs text-slate-400">
                Unique identifier. Lowercase snake_case. Cannot be changed after creation.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-200">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Meta Paid - Mortgage Protection"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-slate-100"
                data-testid="lead-source-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="platform" className="text-slate-200">Platform</Label>
                <Select
                  value={formData.platform}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, platform: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {platformOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="channel" className="text-slate-200">Channel</Label>
                <Select
                  value={formData.channel}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, channel: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {channelOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="playbook" className="text-slate-200">Default Playbook</Label>
              <Input
                id="playbook"
                placeholder="e.g., mortgage_protection"
                value={formData.default_playbook_key}
                onChange={(e) => setFormData(prev => ({ ...prev, default_playbook_key: e.target.value }))}
                className="bg-white/5 border-white/10 text-slate-100"
              />
              <p className="text-xs text-slate-400">
                Optional. Leave blank to not auto-assign a playbook.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="workspace" className="text-slate-200">Default Workspace</Label>
              <Select
                value={formData.default_workspace_id || "none"}
                onValueChange={(v) => setFormData(prev => ({ 
                  ...prev, 
                  default_workspace_id: v === "none" ? "" : v 
                }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                  <SelectValue placeholder="Select workspace..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="none" className="text-slate-400">
                    (None - use webhook key config)
                  </SelectItem>
                  {workspaces.map(ws => (
                    <SelectItem key={ws.id} value={ws.id} className="text-slate-100">
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                Optional. Webhook key workspace takes precedence.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="vertical" className="text-slate-200">Default Vertical</Label>
              <Select
                value={formData.default_vertical_key || "none"}
                onValueChange={(v) => setFormData(prev => ({ 
                  ...prev, 
                  default_vertical_key: v === "none" ? "" : v 
                }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                  <SelectValue placeholder="Select vertical..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="none" className="text-slate-400">
                    (None)
                  </SelectItem>
                  {verticalOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                {formData.channel === "referral_traffic" 
                  ? "Required for Referral Traffic sources unless set on calendar route."
                  : "Optional. Fallback vertical for leads from this source."}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting || !formData.key.trim() || !formData.name.trim() || !!keyError}
              data-testid="create-lead-source-submit"
            >
              {submitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Edit Lead Source</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update lead source configuration. Key cannot be changed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-200">Key</Label>
              <div className="p-2 bg-white/5 border border-white/10 rounded-md font-mono text-slate-400">
                {formData.key}
              </div>
              <p className="text-xs text-slate-500">
                Key is immutable after creation
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-slate-200">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-slate-100"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-200">Platform</Label>
                <Select
                  value={formData.platform}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, platform: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {platformOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-200">Channel</Label>
                <Select
                  value={formData.channel}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, channel: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {channelOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-200">Default Playbook</Label>
              <Input
                value={formData.default_playbook_key}
                onChange={(e) => setFormData(prev => ({ ...prev, default_playbook_key: e.target.value }))}
                className="bg-white/5 border-white/10 text-slate-100"
                placeholder="e.g., mortgage_protection"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-200">Default Workspace</Label>
              <Select
                value={formData.default_workspace_id || "none"}
                onValueChange={(v) => setFormData(prev => ({ 
                  ...prev, 
                  default_workspace_id: v === "none" ? "" : v 
                }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                  <SelectValue placeholder="Select workspace..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="none" className="text-slate-400">
                    (None)
                  </SelectItem>
                  {workspaces.map(ws => (
                    <SelectItem key={ws.id} value={ws.id} className="text-slate-100">
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-200">Default Vertical</Label>
              <Select
                value={formData.default_vertical_key || "none"}
                onValueChange={(v) => setFormData(prev => ({ 
                  ...prev, 
                  default_vertical_key: v === "none" ? "" : v 
                }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                  <SelectValue placeholder="Select vertical..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="none" className="text-slate-400">
                    (None)
                  </SelectItem>
                  {verticalOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                {formData.channel === "referral_traffic" 
                  ? "Required for Referral Traffic unless set on calendar route."
                  : "Fallback vertical when payload doesn't specify one."}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setEditOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={submitting || !formData.name.trim()}
              data-testid="edit-lead-source-submit"
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Key Management Dialog */}
      <Dialog open={keyDialog.open} onOpenChange={(open) => !open && closeKeyDialog()}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Key className="h-5 w-5 text-indigo-400" />
              {keyDialog.action === "generate" && "Generate API Key"}
              {keyDialog.action === "rotate" && "Rotate API Key"}
              {keyDialog.action === "revoke" && "Revoke API Key"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Lead Source: <span className="text-slate-200 font-medium">{keyDialog.source?.name}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Generate/Rotate: Show new key if available */}
            {newApiKey ? (
              <div className="space-y-3">
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-sm text-emerald-300 mb-2">
                    ✓ API Key {keyDialog.action === "generate" ? "Generated" : "Rotated"} Successfully
                  </p>
                  <p className="text-xs text-slate-400 mb-2">
                    Store this key securely — it will not be shown again.
                  </p>
                </div>
                
                {/* Raw API Key */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-400">API Key</label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newApiKey}
                      readOnly
                      className="bg-slate-900 border-slate-700 text-slate-100 font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyApiKey}
                      title="Copy API Key"
                    >
                      {copiedKey ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                {/* Bearer Token (for direct paste into GHL) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-400">Authorization Header Value</label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={`Bearer ${newApiKey}`}
                      readOnly
                      className="bg-slate-900 border-slate-700 text-amber-300 font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyBearerKey}
                      title="Copy Bearer Token"
                    >
                      {copiedBearerKey ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Copy this value directly into GHL's Authorization header field.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Generate action */}
                {keyDialog.action === "generate" && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-300">
                      Generate an API key to enable lead intake for this source.
                    </p>
                    <p className="text-xs text-amber-400/80 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      The key will only be displayed once. Store it securely.
                    </p>
                  </div>
                )}
                
                {/* Rotate action */}
                {keyDialog.action === "rotate" && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-300">
                      Rotating the API key will immediately invalidate the current key.
                    </p>
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-xs text-amber-300 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Any integrations using the old key will stop working immediately.
                      </p>
                    </div>
                    <p className="text-xs text-slate-400">
                      Current fingerprint: <span className="font-mono">{keyDialog.source?.api_key_fingerprint}...</span>
                    </p>
                  </div>
                )}
                
                {/* Revoke action */}
                {keyDialog.action === "revoke" && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-300">
                      Revoking the API key will permanently disable lead intake for this source.
                    </p>
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-red-300 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        This action cannot be undone. You will need to generate a new key.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={closeKeyDialog}
            >
              {newApiKey ? "Done" : "Cancel"}
            </Button>
            {!newApiKey && (
              <>
                {keyDialog.action === "generate" && (
                  <Button
                    onClick={handleGenerateKey}
                    disabled={keyLoading}
                  >
                    {keyLoading ? "Generating..." : "Generate Key"}
                  </Button>
                )}
                {keyDialog.action === "rotate" && (
                  <Button
                    onClick={handleRotateKey}
                    disabled={keyLoading}
                    variant="outline"
                  >
                    {keyLoading ? "Rotating..." : "Rotate Key"}
                  </Button>
                )}
                {keyDialog.action === "revoke" && (
                  <Button
                    onClick={handleRevokeKey}
                    disabled={keyLoading}
                    variant="destructive"
                  >
                    {keyLoading ? "Revoking..." : "Revoke Key"}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadSourceAdminManager;
